import React, { useEffect, useState, useMemo } from 'react';
import { Polygon, InfoBox } from '@react-google-maps/api';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import MeasureTool from 'measuretool-googlemaps-v3/src/index.js';
import {
  setExploreActiveDrawingObject,
  updateExploreObjectById,
  setExploreActiveEditingObject,
  setDistanceMeasuringTotalDistance,
  setDistanceMeasuringTotalArea,
} from 'store/actions';
import { areEqual } from 'utilities';

function DistanceManager({
  mapRef,
  object,
  otherObjectIsEditing,
  borderColor,
  visible,
  isFocused,
  isHighlighted,
  onClick,
  mapType,
  activeDrawingObject,
  activeEditingObject,
  selectedView,
  setExploreActiveDrawingObject,
  updateExploreObjectById,
  setExploreActiveEditingObject,
  setDistanceMeasuringTotalDistance,
  setDistanceMeasuringTotalArea,
}) {
  const [measureTool, setMeasureTool] = useState(null); // Distance measurement ref
  const [coordinates, setCoordinates] = useState([]); // Local transient copy of drawn coords
  const [totalData, setTotalData] = useState(null); // Data on distance/area, updates as user draws/edits

  const isDrawing = activeDrawingObject?.id === object.id;
  const isEditing = activeEditingObject?.id === object.id;
  const isFrozen = !isDrawing && !isEditing

  const customOptions = { // Custom parameters for MeasureTool
    mapType,
    visible,
    isNodeTextVisible: false, // Show cumulative distance at every point
    isVisibleTextForShortLines: false, // Show/hide segment length text for short lines
    setCoordinates: prepareCoordinateData, // Update point coordinates as drawing changes
    setTotalData, // Update total area and total distance as drawing changes
    isFrozen,
    size: object.size,
    borderColor,
    onClick,
    clickable: !otherObjectIsEditing
  };

  function prepareCoordinateData(coords) {
    if (isEqual(coordinates, coords)) return;
    const newCoordinates = coords.map(item => {
      return { lat: item[1], lng: item[0] }
    });
    setCoordinates(newCoordinates);
  }

  useEffect(() => {
    if (measureTool || !mapRef?.current) return;
    const tool = new MeasureTool(mapRef.current, {
      unit: 'imperial',
      contextMenu: false,
      customOptions
    });
    tool.addListener('measure_change', onMeasureChange);
    tool.addListener('measure_end', onMeasureEnd);
    if (object.data?.coordinates) { // Add DM to map for previously saved objects
      tool.start(object.data?.coordinates);
    }
    setMeasureTool(tool);

    return () => { // Remove DM from map when object is unmounted
      tool && tool.end();
    }
  }, [mapRef]);

  const onMeasureChange = (measureEvent) => {
    if (measureEvent?.result) {
      const { lengthText, areaText, points } = measureEvent.result;
      if (points.length === 0) {
        return;
      }
      setDistanceMeasuringTotalDistance(lengthText);
      setDistanceMeasuringTotalArea(areaText);
    }
  }
  const onMeasureEnd = (measureEvent) => {
    setCoordinates([]); // When DM is cleared, clear coordinates to remove polygon
  }

  useEffect(() => { // Update customOptions as mapType changes
    if (!measureTool || !mapRef?.current) return;
    measureTool.setOption('customOptions', customOptions);
  }, [mapRef, measureTool, object.size, isFrozen, mapType, otherObjectIsEditing, borderColor, visible]);

  useEffect(() => { // Workaround for only the last created DM instance being editable- restart tool on edit start
    if (!measureTool || !isEditing) return;
    measureTool.end(); // Clear previous instance
    setTimeout(() => { // Wait until previous instance is cleared
      measureTool.start(object.data?.coordinates); // Restart with same coordinates
    }, 10);
  }, [isEditing, object.data?.coordinates]);

  useEffect(() => { // Change border size and color when focusing object
    if (isFocused || isHighlighted) {
      if (!measureTool || !mapRef?.current) return;
      measureTool.setOption('customOptions', {
        ...customOptions,
        size: customOptions.size * 2,
        borderColor: isHighlighted ? '#fff700' :'#ffa300'
      });
    } else if (isFocused === false || isHighlighted === false) {
      if (!measureTool || !mapRef?.current) return;
      measureTool.setOption('customOptions', customOptions);
    }
  }, [isFocused, isHighlighted])

  const clearObject = () => {
    measureTool.end(); // Remove drawing from map
    setDistanceMeasuringTotalDistance('0 mi');
    setDistanceMeasuringTotalArea('0 mi²');
  }

  useEffect(() => {
    if (isDrawing && measureTool) { // User clicks Set Position to begin initial drawing
      measureTool.start(); // Begin drawing
    }
    if (!isDrawing && measureTool && !object.data) { // User draws but clicks cancel before ever setting position
      clearObject();
    }
  }, [isDrawing, measureTool]);

  useEffect(() => {
    if (measureTool?.length && activeDrawingObject) { // Enable Set button once user draws something with length > 0
      setExploreActiveDrawingObject({ ...activeDrawingObject, isPlaced: true });
    }
  }, [measureTool?.length]);

  useEffect(() => {
    if (activeDrawingObject?.isCreating && coordinates.length) { // User clicks Set to create initial drawing
      const { id } = activeDrawingObject;
      const data = { coordinates, totalData };
      updateExploreObjectById(id, { data });
      setExploreActiveDrawingObject(null);
    }
  }, [activeDrawingObject?.isCreating]);

  useEffect(() => {
    if (!isEditing) return;
    if (activeEditingObject?.isCancelling) { // User clicks Cancel while editing
      const activeObject = selectedView.objects.find(
        object => object.id === activeEditingObject.id
      )?.data;
      if (activeObject?.totalData) {
        const { totalDistance, totalArea } = activeObject.totalData;
        setDistanceMeasuringTotalDistance(totalDistance);
        setDistanceMeasuringTotalArea(totalArea);
      }
      if (activeObject) {
        measureTool.end();
        measureTool.start(activeObject.coordinates);
      }
    }

    if (activeEditingObject?.isSaving) { // User clicks Done while editing
      const { id } = activeEditingObject;
      const data = { coordinates, totalData };
      updateExploreObjectById(id, { data });
      setExploreActiveEditingObject(null);
    }
  }, [activeEditingObject]);

  useEffect(() => {
    if (!object.data && measureTool) { // User clicks Clear to remove existing drawing
      clearObject();
    }
  }, [object.data]);

  function getTotalPopup() {
    const { isTitleVisible, isMeasuringVisible, description } = object || {};
    const isVisible = isTitleVisible || isMeasuringVisible;
    if (!isVisible || !coordinates?.length) return null;

    function getInfoBoxPosition(a, b) { // set the infobox at the highest latitude position
      return a.lat > b.lat ? a : b;
    }

    function onLoadInfoBox(infoBox) {
      infoBox.boxClass = infoBox.boxClass + ' infoBoxContainer';
    }

    const position = coordinates.reduce(getInfoBoxPosition);
    return (
      position &&
        <InfoBox
          position={position}
          visible={isVisible}
          onLoad={onLoadInfoBox}
          options={{
            disableAutoPan: true
          }}
        >
          <div className="measuring-box">
            {isTitleVisible && <div className={classNames('title-font-weight', isMeasuringVisible && 'title-margin')}>{description}</div>}
            {isMeasuringVisible && totalData &&
              <>
                <div>{`Total distance: ${totalData?.totalDistance}`}</div>
                <div>{`Total area: ${totalData?.totalArea}`}</div>
              </>
            }
          </div>
        </InfoBox>
    );
  }

  const paths = useMemo(() => {
    if (isEqual(coordinates[0], coordinates[coordinates.length - 1])) { // If coordinates form a closed polygon
      return coordinates;
    }
    return [];
  }, [coordinates]);

  const polygonOptions = {
    clickable: !isDrawing && !isEditing && !otherObjectIsEditing,
    fillColor: isHighlighted ? '#fff700' : isDrawing || isEditing || isFocused ? '#ffa300' : object.color[mapType],
    strokeColor: 'transparent',
  }

  return (
    <>
      {object.visible && totalData && !isEditing && !isDrawing ? getTotalPopup() : null}
      <Polygon
        visible={visible}
        paths={paths}
        options={polygonOptions}
        onClick={onClick}
      />
    </>
  )
}

function mapState({ exploreMap, exploreViews }) {
  return {
    mapType: exploreMap.mapType,
    activeDrawingObject: exploreViews.activeDrawingObject,
    activeEditingObject: exploreViews.activeEditingObject,
    selectedView: exploreViews.selectedView,
  };
}

const mapDispatch = {
  setExploreActiveDrawingObject,
  updateExploreObjectById,
  setExploreActiveEditingObject,
  setDistanceMeasuringTotalDistance,
  setDistanceMeasuringTotalArea,
}

export default compose(
  connect(mapState, mapDispatch)
)(React.memo(DistanceManager, areEqual));
